import type { FolderNode } from '@/components/MantineFileManager/file.type';
import type { DirectusFolders, FileSubmissionDirectusFiles } from '@/models/types';

export const getRelationId = (relation?: string | DirectusFolders | null) =>
  typeof relation === 'string' ? relation : (relation?.id ?? null);

export const formatFileSize = (value?: number | null) => {
  if (!value || value <= 0) {
    return '—';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

export const buildFolderTree = (folders: DirectusFolders[]) => {
  const nodesById = new Map<string, FolderNode>();
  const roots: FolderNode[] = [];

  folders.forEach(folder => {
    nodesById.set(folder.id, {
      children: [],
      folder,
    });
  });

  folders.forEach(folder => {
    const node = nodesById.get(folder.id);

    if (!node) {
      return;
    }

    const parentId = getRelationId(folder.parent);
    const parentNode = parentId ? nodesById.get(parentId) : undefined;

    if (parentNode) {
      parentNode.children.push(node);
      return;
    }

    roots.push(node);
  });

  const sortNodes = (nodes: FolderNode[]) => {
    nodes.sort((left, right) => left.folder.name.localeCompare(right.folder.name));
    nodes.forEach(node => sortNodes(node.children));
  };

  sortNodes(roots);

  return roots;
};

export const findFolderPath = (folders: DirectusFolders[], folderId: string | null) => {
  if (!folderId) {
    return [] as DirectusFolders[];
  }

  const folderById = new Map(folders.map(folder => [folder.id, folder] as const));
  const path: DirectusFolders[] = [];
  let currentFolder = folderById.get(folderId);

  while (currentFolder) {
    path.unshift(currentFolder);
    const parentId = getRelationId(currentFolder.parent);
    currentFolder = parentId ? folderById.get(parentId) : undefined;
  }

  return path;
};

export const getFileName = (file: FileSubmissionDirectusFiles, unavailableLabel: string) => {
  const directusFile = file.directus_files_id;

  if (!directusFile) {
    return unavailableLabel;
  }

  if (typeof directusFile === 'string') {
    return directusFile;
  }

  return directusFile.filename_download ?? directusFile.title ?? directusFile.id;
};
